import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row } from 'reactstrap';
import Toast from '../../helpers/Toast';
import { ProfitabilityAssistancesDTO } from '../../models/Profitability/ProfitabilityAssistancesDTO';
import { ProjectProfitabilityDTO } from '../../models/Profitability/ProjectProfitabilityDTO';
import { ProjectProfitabilityParametersDTO } from '../../models/Profitability/ProjectProfitabilityParametersDTO';
import { ProjectDTO } from '../../models/Project/ProjectDTO';
import { ProjectService } from '../../services/ProjectService';
import TableComponent from '../TableComponent';

export type ProjectProfitabilityProps = {
    projectId: string;
    reloadExecutions: boolean;
    loadingUpdate: boolean;
}

function ProjectProfitabilityBilledTable({ projectId, reloadExecutions, loadingUpdate }: ProjectProfitabilityProps) {

    const projectService = new ProjectService();
    const pageSizes: number[] = [50, 100];
    const [data, setData] = useState<ProfitabilityAssistancesDTO[]>([]);
    const [projectProfitability, setProjectProfitability] = useState<ProjectProfitabilityDTO>();
    const [pageSize, setPageSize] = useState<number>(50);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [reload, setReload] = useState<boolean>(false);
    const intl = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCount, setLoadingCount] = useState<number>(0);

    // Orçamento
    const [isLoadingProject, setIsLoadingProject] = useState<boolean>(false);
    const [project, setProject] = useState<ProjectDTO>({} as ProjectDTO);


    const columns = React.useMemo(
        () => [
            {
                Header: "Tipo",
                accessor: "transactionType",
                width: 10,
                Cell: (props: any) => {
                    const { value } = props.cell;
                    const isTotal = value === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div style={cellStyle}>{value}</div>;
                },
            },
            {
                Header: "Documento",
                accessor: "document",
                width: 10,
                Cell: (props: any) => {
                    const { value } = props.cell;
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div style={cellStyle}>{value}</div>;
                },
            },
            {
                Header: "Total Venda Linha",
                accessor: "partsAndHours[0].totalLineSellingPrice",
                width: 10,
                Cell: (props: any) => {
                    const totalLineSellingPrice = props.cell.value;
                    const formattedTotalLineSellingPrice = totalLineSellingPrice ? intl.formatNumber(totalLineSellingPrice, { style: 'currency', currency: 'EUR' }) : '-';
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div className="table_align-right" style={cellStyle}>{formattedTotalLineSellingPrice}</div>;
                },
            },
            {
                Header: "Total Custo Linha",
                accessor: "partsAndHours[0].totalLineCostPrice",
                width: 10,
                Cell: (props: any) => {
                    const totalLineCostPrice = props.cell.value;
                    const formattedTotalLineCostPrice = totalLineCostPrice ? intl.formatNumber(totalLineCostPrice, { style: 'currency', currency: 'EUR' }) : '-';
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div className="table_align-right" style={cellStyle}>{formattedTotalLineCostPrice}</div>;
                },
            },
            {
                Header: "Total Lucro Linha",
                accessor: "partsAndHours[0].totalLineProfit",
                width: 10,
                Cell: (props: any) => {
                    const totalLineProfit = props.cell.value;
                    const formattedTotalLineProfit = totalLineProfit ? intl.formatNumber(totalLineProfit, { style: 'currency', currency: 'EUR' }) : '-';
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div className="table_align-right" style={cellStyle}>{formattedTotalLineProfit}</div>;
                },
            },
        ],
        [],
    );

    const fetchData = async () => {
        setLoading(true);

        // Obter obra
        setIsLoadingProject(true);

        var projectResponse = await projectService.GetByIdErp(projectId);
        if (projectResponse.success === false || project == null) {
            setIsLoadingProject(false);
            Toast.Show("error", projectResponse.message);
            return;
        }
        setIsLoadingProject(false);

        const profitabilityParams: ProjectProfitabilityParametersDTO = {
            projectId: Number(projectResponse.obj?.erpId) ?? 0,
            projectBudget: projectResponse.obj?.budgetSellingPrice ?? 0,
            showBillabledDocuments: true
        };

        const response = await projectService.GetProjectProfitability(profitabilityParams);

        if (response.success === false && response.obj === null) {
            setData([]);
            Toast.Show("error", response.message);
            setLoading(false);
            setLoadingCount(1);
            return;
        }

        const newData: ProfitabilityAssistancesDTO[] = [];

        response.obj.assistances.forEach((doc) => {
            let totalAssistanceProfit = 0;
            doc.partsAndHours.forEach((partAndHour) => {
                newData.push({
                    transactionType: partAndHour.transactionType,
                    document: doc.document,
                    partsAndHours: [partAndHour], // Adicionar linhas e não apenas o documento
                    totalAssistanceProfit: partAndHour.totalLineProfit, // Usar lucro da linha
                });
                totalAssistanceProfit += partAndHour.totalLineProfit; // Somar lucro de cada linha do documento, para o passar abaixo, na linha totalizadora
            });

            // Adicionar linha totalizadora após cada documento
            newData.push({
                transactionType: "Total",
                document: "-",
                partsAndHours: [{
                    transactionType: "",
                    partErpId: "",
                    partDescription: "",
                    partSellingPrice: 0,
                    totalLineSellingPrice: 0,
                    partCostPrice: 0,
                    totalLineCostPrice: 0,
                    partProfit: 0,
                    totalLineProfit: totalAssistanceProfit
                }],
                totalAssistanceProfit: totalAssistanceProfit, // Passar valor para a linha totalizadora
            });
        });

        setData(newData);
        setProjectProfitability(response.obj);

        setLoading(false);
        setLoadingCount(1);
    };

    useEffect(() => {
        if (reloadExecutions || loadingUpdate) {
            fetchData();
            setReload(false);
        }
    }, [reloadExecutions, loadingUpdate]);

    return (
        <div>
            <Row style={{ margin: "1rem 0px" }}>
                <h5><FormattedMessage id={"Screens.Projects.Profitability.Title"}></FormattedMessage></h5>
                <br></br>
                <br></br>

                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        <p>
                            <strong>
                                <FormattedMessage id={"Screens.Projects.Profitability.Predicted"} />
                            </strong>{" "}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Projects.Profitability.BudgetSellingPrice"} />
                            {intl.formatNumber(projectProfitability?.budgetSellingPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Projects.Profitability.BudgetCostPrice"} />
                            {intl.formatNumber(projectProfitability?.budgetCostPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Projects.Profitability.PredictedProfit"} />
                            {intl.formatNumber(projectProfitability?.predictedProfit ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p>
                            <FormattedMessage id={"Screens.Projects.Profitability.PredictedSellingMargin"} />
                            {intl.formatNumber((projectProfitability?.predictedSellingMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>

                        <p>
                            <FormattedMessage id={"Screens.Projects.Profitability.PredictedCostMargin"} />
                            {intl.formatNumber((projectProfitability?.predictedCostMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ marginRight: '1.5rem' }}>
                            <strong>
                                <FormattedMessage id={"Screens.Projects.Profitability.Real"} />
                            </strong>{" "}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.TotalSellingPrice"} />
                            {intl.formatNumber(projectProfitability?.totalProjectSellingPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.TotalCostPrice"} />
                            {intl.formatNumber(projectProfitability?.totalProjectCostPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.TotalProfit"} />
                            {intl.formatNumber(projectProfitability?.totalProjectProfit ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.RealSellingMargin"} />
                            {intl.formatNumber((projectProfitability?.realSellingMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.RealCostMargin"} />
                            {intl.formatNumber((projectProfitability?.realCostMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                    </div>
                </div>
                <br></br>
                <TableComponent
                    columns={columns}
                    data={data}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={pageCount}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoading}
                    fetchData={fetchData}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
            </Row>
            {loading === true && loadingCount !== 1 && (
                <div className="loading">
                    <span>
                        {" "}
                        <FormattedMessage id="Common.Loading.DataLoading" />
                        ...{" "}
                    </span>
                    <CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                </div>
            )}
        </div>
    )
}

export default ProjectProfitabilityBilledTable